'use client';

// ui-kit/primitives.js: de CRM-vrije bouwstenen van de PearsonApp (portaalplan U1).
// EEN definitie per primitief: components/HomeV2/lib.js heruitvoert ze voor de CRM,
// het portaal leest ze via ui-kit/index.js. Alleen react, react-dom en ui-kit/ zelf
// (bewaakt door lib/ui-kit-isolation.test.mjs).

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { HV2, SURFACE, TONE_INFO, TONE_QUIET } from './tokens.mjs';
import { BRANCH_PATHS } from './icons.mjs';
import { CV_SEND_PATHS } from './kpi-icons.mjs';
import { closeHairlineGrid, hairlineGapPx } from './hairline-grid.mjs';

// placeTooltip: the ONE viewport-aware placement math shared by every hover
// popover (InfoHint, HoverCard). Given the trigger's rect plus the popover's
// width/height, returns { top, left } in FIXED (viewport) coordinates: hang
// below the trigger, aligned to the requested edge, then clamp so the box stays
// 8px inside the viewport - shift left if it would overrun the right edge, flip
// above the trigger if it would overrun the bottom. Pass height 0 before the box
// is measured (places below); a follow-up call with the measured height applies
// the flip. This is why a tooltip is NEVER clipped by an ancestor overflow or
// the screen edge - it lives in a document.body portal, positioned here.
// `side` 'left' | 'right' puts the box BESIDE the trigger, top edges level, and
// swaps sides when the preferred side has no room: for a row inside a click menu,
// where a box below would cover the rows under it (eigenaarsbesluit 13 sep 2026).
export function placeTooltip(rect, width, height, align = 'left', side = 'bottom') {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  if (side === 'left' || side === 'right') {
    let left = side === 'left' ? rect.left - 8 - width : rect.right + 8;
    if (side === 'left' && left < 8) left = rect.right + 8;
    if (side === 'right' && left + width > vw - 8) left = rect.left - 8 - width;
    left = Math.max(8, Math.min(left, vw - 8 - width));
    let top = rect.top;
    if (height && top + height > vh - 8) top = Math.max(8, vh - 8 - height);
    return { top, left };
  }
  let left = align === 'right' ? rect.right - width : rect.left;
  if (left + width > vw - 8) left = vw - 8 - width; // overflow right -> shift left
  if (left < 8) left = 8;
  let top = rect.bottom + 6; // default: below the trigger
  if (height && top + height > vh - 8) { // would overflow bottom -> flip above
    const above = rect.top - 6 - height;
    top = above >= 8 ? above : Math.max(8, vh - 8 - height);
  }
  return { top, left };
}

// InfoHint: an (i) icon (or any custom trigger) with a REAL hover/focus tooltip.
// Native title= tooltips are flaky and often never render, so we draw our own
// popover (white face, neutral shadow). Pass `text` for the content; pass
// `children` to use a custom trigger (e.g. a warning triangle) instead of the
// default info circle. `align` picks which edge the popover hangs from.
//
// The popover renders through a document.body portal at position:fixed, anchored
// to the trigger's live getBoundingClientRect() (re-placed on scroll/resize while
// open). That's the fix for the recurring clip bug: an ancestor overflow:hidden
// (RailCard/Section) or the viewport edge can no longer cut the tooltip off -
// placeTooltip clamps it to stay on-screen. pointerEvents:none so it never
// intercepts a click. API (text/width/children/align) is unchanged.
export function InfoHint({ text, width = 260, children, align = 'left', color }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = anchorRef.current; if (!el) return;
    const h = tipRef.current ? tipRef.current.offsetHeight : 0;
    setPos(placeTooltip(el.getBoundingClientRect(), width, h, align));
  }, [width, align]);
  const show = () => { place(); setOpen(true); };
  // While open, re-measure (now that the box is mounted, so the bottom-flip can
  // apply) and follow the trigger on scroll/resize.
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);
  return (
    <span ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <span tabIndex={0} onFocus={show} onBlur={() => setOpen(false)}
        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', color: color || HV2.muted, outline: 'none' }}>
        {children || (
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
        )}
      </span>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <span ref={tipRef} role="tooltip" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9700, pointerEvents: 'none', width, ...SURFACE.tooltip, padding: '8px 10px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.45, color: HV2.ink2, whiteSpace: 'normal' }}>
          {text}
        </span>, document.body)}
    </span>
  );
}

// Count badge: the outline pill that shows a number beside a label.
// Badge (eigenaarsregel 17 sep 2026): EEN badge-primitief voor acroniemen,
// tags en tellingen. Vervangt Sym, Pill en CountBadge. Ontwerp B: 1.5px
// inkrand, witte vulling, radius 3, fsBadge/700, pad 1px 6px. active=true
// (voor tellingen) trekt de rand naar ink, anders border. color overschrijft
// de randkleur (voor gekleurde IV-badges). filled zet bgSub als vulling.
export function Badge({ children, active = false, color, filled = false, title, style }) {
  const bc = color || (active ? HV2.ink : HV2.border);
  return <span title={title} style={{ display: 'inline-block', border: `1.5px solid ${bc}`, color: HV2.ink, background: filled ? HV2.bgSub : HV2.bg, borderRadius: 3, padding: '1px 6px', fontSize: HV2.fsBadge, fontWeight: 650, lineHeight: '14px', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 18, textAlign: 'center', ...style }}>{children}</span>;
}
// DE neutrale tekst-chip (eigenaarsbesluit 17 juli 2026): ELKE statische
// informatieve chip/label in de app is dit component of TonePill (semantische
// status). Nooit meer inline spans met eigen border/radius/padding - de
// design-guard-hook en lib/design-chips.test.mjs bewaken dat. Contract:
// radius 4, hairline HV2.border, bgSub-vulling, inkt-tekst, fsText/600.
export function Chip({ children, title, style }) {
  return <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${HV2.border}`, color: HV2.ink2, background: HV2.bgSub, borderRadius: 4, padding: '1px 8px', fontSize: HV2.fsText, fontWeight: 560, lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0, ...style }}>{children}</span>;
}

// HET haarlijnraster (DESIGN-SYSTEM.md Regel 39, eigenaarsregel 17 september
// 2026): Kerngegevens op elk recordvenster, het formulier Nieuwe kandidaat en de
// kwalificatie zijn dit component. Cellen dragen HAIRLINE_CELL (wit, 9/14
// padding), een cel over de volle breedte HAIRLINE_CELL_FULL. Niemand telt
// cellen: het raster sluit zichzelf via ui-kit/hairline-grid.mjs op de echte DOM,
// na elke render en bij elke wijziging in de kinderen (een cel die pas na een
// fetch verschijnt, een cel die een component niet rendert). Een ongepaarde
// halve cel spant de rest van zijn rij. Vulcellen, een % 2 formule of een
// handgeschreven raster met gap 1 op HV2.divider zijn verboden; de ratchet in
// lib/design-kerngegevens.test.mjs bewaakt dat.
export const HAIRLINE_CELL = Object.freeze({ background: HV2.bg, padding: '9px 14px', minWidth: 0 });
export const HAIRLINE_CELL_FULL = Object.freeze({ ...HAIRLINE_CELL, gridColumn: '1 / -1' });
export const HAIRLINE_LABEL = Object.freeze({ fontSize: HV2.fsText, fontWeight: 560, color: HV2.muted, marginBottom: 3 });
export const HAIRLINE_VALUE = Object.freeze({ fontSize: HV2.fsText, color: HV2.ink, wordBreak: 'break-word' });

// De gap volgt de pixelverhouding (browserzoom, extern scherm): zie
// hairlineGapPx. Een matchMedia op de huidige resolutie meldt elke zoomstap.
function useHairlineGap() {
  const [gap, setGap] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mq = null;
    const sync = () => {
      setGap(hairlineGapPx(window.devicePixelRatio));
      if (mq) mq.removeEventListener('change', sync);
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mq.addEventListener('change', sync);
    };
    sync();
    return () => { if (mq) mq.removeEventListener('change', sync); };
  }, []);
  return gap;
}

export const HairlineGrid = forwardRef(function HairlineGrid({ cols = 2, style, children, ...rest }, fwd) {
  const gap = useHairlineGap();
  const inner = useRef(null);
  const setRef = useCallback((el) => {
    inner.current = el;
    if (typeof fwd === 'function') fwd(el);
    else if (fwd) fwd.current = el;
  }, [fwd]);
  // Geen deps: elke render van de aanroeper kan cellen wisselen. De observer
  // vangt de rest (een kind dat zelf rendert). Alleen inline gridColumn wordt
  // gelezen, dus dit dwingt geen layout af.
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return undefined;
    const run = () => closeHairlineGrid(el, cols);
    run();
    if (typeof MutationObserver !== 'function') return undefined;
    const mo = new MutationObserver(run);
    mo.observe(el, { childList: true });
    // cols 'auto' (auto-fit, template uit style): de kolomtelling verandert
    // met de breedte, dus de sluiting loopt ook na elke resize.
    const ro = cols === 'auto' && typeof ResizeObserver === 'function' ? new ResizeObserver(run) : null;
    if (ro) ro.observe(el);
    return () => { mo.disconnect(); if (ro) ro.disconnect(); };
  });
  const auto = cols === 'auto';
  const n = auto ? 'auto' : Math.max(1, Number(cols) || 1);
  return (
    <div ref={setRef} data-hairline-grid={n} style={{ display: 'grid', ...(auto ? {} : { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }), gap, background: HV2.divider, ...style }} {...rest}>
      {children}
    </div>
  );
});

// Segmented control - the ONE toggle pattern (Fase/Bord, Mij/Team, category
// filters). opts: [{ k, label, node? }]; value/onChange controlled.
// Active segment is a raised white chip on a subtle grey track; never the kobalt
// primary-button fill and never a full-black fill (those read as actions).
// `dense` maakt de totale hoogte 30 (22 + 2x3 rand + 2x1 border), gelijk aan de
// md-knop en de filtertriggers, voor rijen waar de toggle NAAST knoppen staat en
// het hoogteverschil anders scheef oogt (bellijstkop, eigenaarsverzoek 11 aug).
// `colors` (optioneel) hertint het spoor voor een donker vlak (Targets-terminal):
// { track, border, chip, ink, muted }; ontbrekende sleutels vallen terug op HV2.
// `fill` rekt het spoor over de hele breedte en verdeelt de segmenten gelijk
// (formuliervelden zoals Ja/Nee). Per optie mag `disabled`, `title` en `ariaLabel`
// mee (een segment met alleen een glyph als node).
// Opnieuw klikken op het actieve segment roept onChange gewoon aan; wie een
// leeg-toestand wil, vertaalt dat zelf (value === k ? '' : k).
export function SegControl({ value, onChange, opts, dense = false, colors = null, fill = false, style: outer = null }) {
  const c = { track: HV2.subtle, border: HV2.border, chip: HV2.bg, ink: HV2.ink, muted: HV2.muted, ...(colors || {}) };
  return (
    <div style={{ display: fill ? 'flex' : 'inline-flex', gap: 2, background: c.track, border: `1px solid ${c.border}`, borderRadius: 7, padding: 3, ...(outer || {}) }}>
      {opts.map((o) => {
        const on = value === o.k;
        const style = { display: 'inline-flex', alignItems: 'center', justifyContent: fill ? 'center' : undefined, flex: fill ? 1 : undefined, gap: 6, height: dense ? 22 : 26, padding: dense ? '0 10px' : '0 14px', borderRadius: 4, border: 'none', background: on ? c.chip : 'transparent', color: on ? c.ink : c.muted, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: on ? 560 : 480, cursor: o.disabled ? 'not-allowed' : 'pointer', opacity: o.disabled ? 0.5 : 1, whiteSpace: 'nowrap', boxShadow: on ? `0 1px 2px rgba(0,0,0,0.10), 0 0 0 1px ${c.border}` : 'none', textDecoration: 'none' };
        return o.href
          ? <a key={o.k} {...hashNav(o.href, () => onChange(o.k))} aria-current={on ? 'page' : undefined} title={o.title} style={style}>{o.node || o.label}</a>
          : <button key={o.k} type="button" disabled={!!o.disabled} onClick={() => onChange(o.k)} aria-pressed={on} aria-label={o.ariaLabel} title={o.title} style={style}>{o.node || o.label}</button>;
      })}
    </div>
  );
}

// TonePill: het canonieke semantische status-label (tonen in ui-kit/tokens.mjs).
export function TonePill({ tone = 'red', wrap = false, children }) {
  const map = {
    red: { bg: HV2.red, fg: '#ffffff' },
    amber: { bg: HV2.orange, fg: '#ffffff' },
    orange: { bg: HV2.orange, fg: '#ffffff' },
    green: { bg: HV2.green, fg: '#ffffff' },
    blue: { bg: TONE_INFO, fg: HV2.ink2 },
    accent: { bg: TONE_INFO, fg: HV2.ink2 },
    slate: { bg: TONE_INFO, fg: HV2.ink2 },
    neutral: { bg: TONE_QUIET, fg: HV2.ink2 },
  };
  const c = map[tone] || map.red;
  // Radius 4 en dezelfde geometrie als EntityChip (fsText/700, regelhoogte 18,
  // padding 2px 10px): een statuspill en een typechip staan naast elkaar in
  // dezelfde rij en mogen niet in maat verschillen. 'Nooit vol rond'.
  return <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: c.fg, background: c.bg, borderRadius: 4, padding: '2px 10px', lineHeight: '18px', whiteSpace: wrap ? 'normal' : 'nowrap', ...(wrap ? { display: 'inline-block', maxWidth: '100%', boxSizing: 'border-box' } : null), flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{children}</span>;
}

// ── Internal record navigation: open-in-new-tab support ─────────────────────
// The app is hash-routed (#crm-<entity>-<slug>, #proces-<id>) and every record
// hash deep-links on a fresh load, so a real <a href="#…"> lets the browser open
// a record in a new tab natively. hashNav turns a hash href into anchor props:
// middle-click needs no JS at all (the native <a href> handles auxclick), and the
// onClick lets the browser take ANY modified click (Cmd/Ctrl/Shift/Alt, or the
// middle button) while a PLAIN click stays in-app via onActivate (no full reload).
// Omit onActivate to only claim modified clicks and let a plain click fall through
// to a surrounding handler (e.g. a table row whose own onClick expands/opens it).
export function hashNav(href, onActivate) {
  return {
    href,
    onClick: (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();
      if (onActivate) onActivate(e);
    },
  };
}

// ── Entity icons (one source of truth) ───────────────────────────────────────
// Used by the CRM tabs, the left rail CRM submenu, record drawers, the command
// palette and anywhere an entity is named, so a candidate/client/prospect/job/
// contact/bellijst/proces always shows the same glyph.
export const ENT_STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
// Gevulde silhouetten (eigenaarsbesluit 28 aug 2026). De lijnversies vielen bij
// 13px uit elkaar en drie procesglyphs waren identiek; nu draagt elk type een
// eigen dichte vorm en zit het detail als uitsparing IN de vorm (fill-rule
// evenodd), niet als tweede lijn ernaast. Eén pad per glyph, zodat kleur alleen
// via `fill` loopt en een aanroeper nooit per ongeluk een lijnversie krijgt.
// Uitzondering: elk proces gebruikt het lijn-takglyph (BRANCH_PATHS), zowel
// de navigatie als alle procestracks (eigenaarsbesluit 4 oktober 2026).
export const ENT_FILL = (d) => <path fillRule="evenodd" d={d} />;
// Lijnglyphs voor de acht CRM-typen (eigenaarsbesluit 19 okt 2026): kandidaat,
// klant, prospect, contact, job, lijst, interviewverzoek en CV Send tekenen als
// lijnwerk in het gewicht van de rail (stroke 2, ronde uiteinden), net als het
// takglyph van een proces. Dit vervangt voor deze acht de gevulde silhouetten van
// 28 aug 2026; de overige typen blijven gevuld.
export const ENT_LINE = (ds) => <>{ds.map((d, i) => <path key={i} d={d} />)}</>;
export const ENT_LINES = {
  candidate: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  contact: ['M3 5h18v14H3z', 'M9 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', 'M6 16a3 3 0 0 1 6 0', 'M15 10h3', 'M15 14h3'],
  company: ['M3 21h18M6 21V8l6-4 6 4v13M10 21v-5h4v5'],
  prospect: ['M3 21h10', 'M6 21V8l6-4 6 4v3.5', 'M16.5 19.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M18.7 18.7 21 21'],
  job: ['M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16', 'M2 11h20', 'M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z'],
  callList: ['M3 6h12', 'M3 12h12', 'M3 18h7', 'M16 14l5 3.5-5 3.5z'],
  interviewRequest: ['M3 5h18v16H3z', 'M3 9h18', 'M8 3v4', 'M16 3v4', 'M9 15l2 2 4-4'],
  // Het document met pijl van CV Send is letterlijk CV_SEND_PATHS (kpi-icons.mjs).
  cvSend: CV_SEND_PATHS,
};
export const ENT_PATHS = {
  candidate: ENT_LINE(ENT_LINES.candidate),
  // Visitekaartje, GEEN tweede persoon: een losse mensfiguur was op 13px niet
  // van de kandidaat te onderscheiden.
  contact:   ENT_LINE(ENT_LINES.contact),
  company:   ENT_LINE(ENT_LINES.company),
  prospect:  ENT_LINE(ENT_LINES.prospect),
  job:       ENT_LINE(ENT_LINES.job),
  // Bellijst: drie regels met een afspeelpijl, je werkt hem van boven naar
  // beneden af. Was een pen, en die zei bewerken in plaats van bellen.
  callList:  ENT_LINE(ENT_LINES.callList),
  // Call log: gevulde handset, zelfde gewicht als de andere entiteitsglyphs.
  callLog:   ENT_FILL('M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z'),
  // Interviewverzoek (CRM-tab 'Interviewverzoeken'): agenda met vinkje, een
  // klant deelde een moment via de publieke CV-link.
  interviewRequest: ENT_LINE(ENT_LINES.interviewRequest),
  // CV Send: het document met pijl (CV_SEND_PATHS). Als proces is het process.
  cvSend:    ENT_LINE(ENT_LINES.cvSend),
  applicant: ENT_FILL('M8.5 1.6h7a1.2 1.2 0 0 1 1.2 1.2v1.6H7.3V2.8a1.2 1.2 0 0 1 1.2-1.2Z M4.2 4h15.6a1.6 1.6 0 0 1 1.6 1.6v15.8a1.6 1.6 0 0 1-1.6 1.6H4.2a1.6 1.6 0 0 1-1.6-1.6V5.6a1.6 1.6 0 0 1 1.6-1.6Z M12 7.4a2.6 2.6 0 1 0 0 5.2a2.6 2.6 0 1 0 0-5.2Z M7 18.8c0-2.2 2.2-3.6 5-3.6s5 1.4 5 3.6Z'),
  process:   <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>,
  // Kwalificatie (eigenaarskeuze 15 sep 2026): een dossiermap met tab. De tab
  // breekt de bovenrand, en dat doet geen enkel ander glyph in deze tabel, dus de
  // silhouet blijft op 13px herkenbaar in een dichte lijstrij. Het type staat als
  // uitsparing in de map: de kandidaatfiguur of de koffer, precies de vormen die
  // 'candidate' en 'job' hierboven al dragen, zodat de familie klopt. Tot deze
  // datum waren dit twee lijnglyphs in Kwalificaties.js (een persoon met vinkje,
  // een koffer met plusje): beide lazen als 'goedgekeurd' en 'nieuw', geen van
  // beide als kwalificatie, en lijn hoort niet bij een recordtype (designregel 7).
  qualCandidate: ENT_FILL('M3.4 3.6h5.6l2 2.4h9.6a1.8 1.8 0 0 1 1.8 1.8v11.8a1.8 1.8 0 0 1-1.8 1.8H3.4a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8Z M12 9.6a2.1 2.1 0 1 0 0 4.2a2.1 2.1 0 1 0 0-4.2Z M8.4 18c0-2 1.6-3.1 3.6-3.1s3.6 1.1 3.6 3.1Z'),
  qualJob:       ENT_FILL('M3.4 3.6h5.6l2 2.4h9.6a1.8 1.8 0 0 1 1.8 1.8v11.8a1.8 1.8 0 0 1-1.8 1.8H3.4a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8Z M10.4 8.8h3.2a1.1 1.1 0 0 1 1.1 1.1v.9h-1.7v-.5h-2v.5h-1.7v-.9a1.1 1.1 0 0 1 1.1-1.1Z M7.8 11.4h8.4v5.8H7.8Z'),
  // Sjabloon (U3, 12 okt 2026): de drie delen van MenuIcon fileLines gevuld, het
  // blad als silhouet, de omgeslagen hoek en de twee tekstregels als uitsparing.
  template:  ENT_FILL('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z M14 2v6h6Z M8 12.2h5v1.6H8Z M8 16.2h7v1.6H8Z'),
  // De stand Mijn en Team van de teambalk (13 okt 2026): een hele gestalte, hoofd,
  // romp en twee benen, en drie gestalten naast elkaar (de middelste groter). De
  // figuren raken elkaar niet, anders snijdt evenodd een gat waar ze overlappen.
  personSolo: ENT_FILL('M12 1.6a2.6 2.6 0 1 0 0 5.2a2.6 2.6 0 1 0 0-5.2Z M8.6 8h6.8a2 2 0 0 1 2 2v5.4h-2.2V22h-2.4v-6.2h-1.6V22H8.8v-6.6H6.6V10a2 2 0 0 1 2-2Z'),
  personTeam: ENT_FILL('M12 1.2a2.3 2.3 0 1 0 0 4.6a2.3 2.3 0 1 0 0-4.6Z M10.4 6.6h3.2a1.6 1.6 0 0 1 1.6 1.6V14h-1.1v8h-1.7v-6.6h-.8V22H9.9v-8H8.8V8.2a1.6 1.6 0 0 1 1.6-1.6Z M4.3 4.4a2 2 0 1 0 0 4a2 2 0 1 0 0-4Z M2.8 9.2h3a1.4 1.4 0 0 1 1.4 1.4v4.8h-1V22H4.7v-6h-.8v6H2.4v-6.6h-1v-4.8a1.4 1.4 0 0 1 1.4-1.4Z M19.7 4.4a2 2 0 1 0 0 4a2 2 0 1 0 0-4Z M18.2 9.2h3a1.4 1.4 0 0 1 1.4 1.4v4.8h-1V22h-1.5v-6h-.8v6h-1.5v-6.6h-1v-4.8a1.4 1.4 0 0 1 1.4-1.4Z'),
};
export const ENT_STROKE_KEYS = new Set(['process', ...Object.keys(ENT_LINES)]);
export const ENT_ALIAS = { client: 'company', prospect: 'prospect', kandidaat: 'candidate', bedrijf: 'company', bellijst: 'callList', hotlist: 'callList', proces: 'process', interviewProces: 'process', bdProces: 'process', placement: 'process', bd: 'process', sourcing: 'process', sollicitant: 'applicant', kwalificatie: 'qualCandidate', kandidaatKwalificatie: 'qualCandidate', jobKwalificatie: 'qualJob', website: 'template' };

// strokeWidth telt alleen nog voor het takglyph (`process`); de gevulde glyphs
// negeren hem. De prop blijft in de signatuur staan omdat ruim honderd aanroepen
// hem meegeven - hem weghalen zou een sweep door dertig bestanden zijn zonder
// dat er iets aan het beeld verandert.
export function EntityIcon({ entity, size = 13, color = 'currentColor', strokeWidth = 2, style }) {
  const key = ENT_ALIAS[entity] || entity;
  const paths = ENT_PATHS[key] || ENT_PATHS.candidate;
  const box = { width: size, height: size, viewBox: '0 0 24 24', style: { flexShrink: 0, display: 'block', ...style } };
  if (ENT_STROKE_KEYS.has(key)) return <svg {...box} {...ENT_STROKE} stroke={color} strokeWidth={strokeWidth}>{paths}</svg>;
  return <svg {...box} fill={color} stroke="none">{paths}</svg>;
}
